/**
 * Doctor browsing and search.
 *
 * Replaces `renderPatientDoctors`, `filterPatientDoctors`,
 * `openPatientCategoryDoctors` and the near-identical `renderDoctors` in app.js.
 *
 * المرحلة 1.3 (P03): the list comes from the server with each doctor's
 * soonest free slot, rating and price (GET /api/doctors?sort=…), and the
 * three sorts — «الأقرب موعدًا», «الأعلى تقييمًا», «الأقل سعرًا» — are the
 * server's, worked out from the same slot board the clinic books from. The
 * scope's bookingJourneys.defaultSort picks the first one (none by default:
 * the order it always had); a channel filter appears when the scope offers
 * more than one. An older server, or one that does not answer, gives the
 * list the app already holds, filtered here as before.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Avatar,
  Badge,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  PageHeader,
  Panel,
  Select,
  Spinner,
} from '../../components/common';
import { useData } from '../../context/DataContext';
import { useDebounced } from '../../hooks/useLocalStorage';
import { doctorApi } from '../../api/endpoints';
import { categoryLabel, categoryName, normalizeDoctor } from '../../domain/catalog';
import { doctorPriceRange, formatCurrency, formatPriceRange, BOOKING_TYPES } from '../../domain/pricing';
import { slotFirstOpen } from '../../domain/discovery';
import { useI18n } from '../../i18n/I18nProvider';
import { ModeBadge, RatingLine, SoonestLine, useBookingPolicy } from '../discovery/parts';
import { CategoryGrid } from './CategoryGrid';

const SORT_BUTTONS = [
  { id: 'soonest', icon: 'clock-3' },
  { id: 'rating', icon: 'star' },
  { id: 'price', icon: 'banknote' },
];

export function PatientDoctors() {
  const { categoryId } = useParams();
  const { doctors, doctorsState, categories, loadDoctors } = useData();
  const { t, lang } = useI18n();
  const policy = useBookingPolicy();

  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryId || '');
  const [sort, setSort] = useState(policy.defaultSort);
  const [mode, setMode] = useState('');
  const sortTouched = useRef(false);
  const debouncedQuery = useDebounced(query, 250);

  const activeCategory = categoryId || selectedCategory;

  // The scope's default sort arrives with the session's capabilities; a sort
  // the patient picked is never overridden by it.
  useEffect(() => {
    if (!sortTouched.current) setSort(policy.defaultSort);
  }, [policy.defaultSort]);

  const discovered = useDiscovery({ sort, mode, categoryId: activeCategory });
  const source = discovered.rows || doctors;

  const filtered = useMemo(() => {
    const needle = debouncedQuery.trim().toLowerCase();
    return source.filter((doctor) => {
      if (!discovered.rows && activeCategory && doctor.category !== activeCategory) return false;
      if (!needle) return true;
      return (
        String(doctor.name).toLowerCase().includes(needle) ||
        String(doctor.title).toLowerCase().includes(needle) ||
        String(doctor.categoryName).toLowerCase().includes(needle) ||
        String(categoryName(doctor.category, categories, 'en')).toLowerCase().includes(needle)
      );
    });
  }, [source, discovered.rows, activeCategory, debouncedQuery, categories]);

  const pickSort = (id) => {
    sortTouched.current = true;
    setSort((current) => (current === id ? 'none' : id));
  };

  const loading = discovered.loading || (doctorsState.loading && doctors.length === 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={activeCategory ? categoryName(activeCategory, categories, lang) : t('discovery.title')}
        subtitle={t('discovery.subtitle', { n: filtered.length })}
        icon="stethoscope"
        actions={
          slotFirstOpen(policy) ? (
            <Link
              to={`/patient/book-by-slot${activeCategory ? `?categoryId=${encodeURIComponent(activeCategory)}` : ''}`}
              className="btn-ghost"
              data-testid="book-by-slot-entry"
            >
              <Icon name="calendar-search" size={16} />
              {t('discovery.bySlotEntry')}
            </Link>
          ) : null
        }
      />

      <Panel>
        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Icon
              name="search"
              size={16}
              className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('discovery.search')}
              aria-label={t('discovery.search')}
              className="ps-9"
            />
          </div>

          {!categoryId && (
            <Select
              value={selectedCategory}
              onChange={(event) => setSelectedCategory(event.target.value)}
              className="sm:w-56"
              aria-label={t('discovery.allSpecialties')}
            >
              <option value="">{t('discovery.allSpecialties')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {categoryLabel(category, lang)}
                </option>
              ))}
            </Select>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="dc-chips" role="group" aria-label={t('discovery.sort.label')}>
            <span className="dc-chip-label">{t('discovery.sort.label')}</span>
            {SORT_BUTTONS.map((button) => (
              <button
                key={button.id}
                type="button"
                className={`dc-chip ${sort === button.id ? 'is-on' : ''}`}
                aria-pressed={sort === button.id}
                onClick={() => pickSort(button.id)}
                data-testid={`sort-${button.id}`}
              >
                <Icon name={button.icon} size={14} />
                {t(`discovery.sort.${button.id}`)}
              </button>
            ))}
          </div>

          {policy.modes.length > 1 && (
            <div className="dc-chips" role="group" aria-label={t('discovery.mode.label')}>
              <span className="dc-chip-label">{t('discovery.mode.label')}</span>
              <button type="button" className={`dc-chip ${mode === '' ? 'is-on' : ''}`} aria-pressed={mode === ''} onClick={() => setMode('')}>
                {t('discovery.mode.all')}
              </button>
              {policy.modes.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`dc-chip ${mode === m ? 'is-on' : ''}`}
                  aria-pressed={mode === m}
                  onClick={() => setMode(m)}
                  data-testid={`mode-${m}`}
                >
                  <ModeBadge mode={m} compact className="!border-0 !bg-transparent !p-0" />
                  {t(`discovery.mode.${m}`)}
                </button>
              ))}
            </div>
          )}
        </div>
      </Panel>

      {/* The specialty grid, as the legacy "اختر التخصص" panel showed it. It sits
          above the results so the grid is how you pick, and the dropdown is the
          shortcut rather than the only way in. */}
      {!categoryId && categories.length > 0 && (
        <Panel>
          <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
            <Icon name="layout-grid" size={18} className="text-cyan-400" />
            {t('discovery.pickSpecialty')}
          </h3>
          <CategoryGrid categories={categories} activeId={selectedCategory} />
        </Panel>
      )}

      {!discovered.rows && doctorsState.error ? (
        <ErrorState error={doctorsState.error} onRetry={loadDoctors} />
      ) : loading && filtered.length === 0 ? (
        <Spinner label={t('discovery.loading')} />
      ) : filtered.length === 0 ? (
        <Panel>
          <EmptyState icon="user-x" title={t('discovery.emptyTitle')} message={t('discovery.emptyMessage')} />
        </Panel>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="doctor-list">
          {/* grid-cols-1, not a bare grid: an implicit track sizes to its widest
              card, which on a phone pushed the whole page sideways. */}
          {filtered.map((doctor) => (
            <DoctorCard key={doctor.id} doctor={doctor} categories={categories} mode={mode} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The server's list for one sort, channel and specialty. `rows` stays null
 * until it answers, and when it cannot, so the screen keeps the list the app
 * already holds.
 */
function useDiscovery({ sort, mode, categoryId }) {
  const [state, setState] = useState({ rows: null, loading: true });
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    doctorApi
      .discover({ sort: sort || 'none', mode: mode || undefined, categoryId: categoryId || undefined })
      .then((raw) => {
        if (!live) return;
        const rows = (Array.isArray(raw) ? raw : [])
          .filter((d) => d && d.DoctorProfile)
          .map((d) => ({ ...normalizeDoctor(d), discovery: d.discovery || null }));
        // An older server ignores the parameters: no `discovery`, nothing gained.
        setState({ rows: rows.some((r) => r.discovery) || rows.length === 0 ? rows : null, loading: false });
      })
      .catch(() => live && setState({ rows: null, loading: false }));
    return () => {
      live = false;
    };
  }, [sort, mode, categoryId]);
  return state;
}

function DoctorCard({ doctor, categories, mode }) {
  const { t, lang } = useI18n();
  const clinicCount = doctor.clinics?.length || 0;
  const hasDiscount = doctor.hasNightDiscount || doctor.hasFirstVisitDiscount;
  const d = doctor.discovery;
  const priceHidden = Boolean(d) && d.price === null;

  // The clinic's price is what the patient is actually charged, so it is what
  // the card has to show. Reading `doctor.price_exam` here quoted the
  // DoctorProfile column default — a patient saw one number on this card and a
  // different one on the booking page.
  const examRange = doctorPriceRange({ doctor, bookingType: BOOKING_TYPES.EXAM });
  const followupRange = doctorPriceRange({ doctor, bookingType: BOOKING_TYPES.FOLLOWUP });
  const remoteMode = mode === 'video' || mode === 'voice';

  return (
    <Panel className="hover-card flex flex-col gap-4" padded={false} data-testid="doctor-card">
      <div className="flex items-start gap-3 p-5 pb-0">
        <Avatar src={doctor.image} name={doctor.name} size={56} />
        <div className="min-w-0 flex-1">
          <Link to={`/patient/doctor/${doctor.id}`} className="block truncate text-base font-bold text-white hover:text-cyan-300">
            {doctor.name}
          </Link>
          <p className="truncate text-xs text-slate-400">
            {doctor.title || categoryName(doctor.category, categories, lang)}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300">
              {categoryName(doctor.category, categories, lang)}
            </Badge>
            {hasDiscount && (
              <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300" icon="percent">
                {t('discovery.card.discounts')}
              </Badge>
            )}
            {d && d.modes && d.modes.length > 1 && d.modes.map((m) => <ModeBadge key={m} mode={m} compact />)}
          </div>
        </div>
      </div>

      {d && (
        <>
          <div className="dc-card__facts">
            <RatingLine avg={d.ratingAvg} count={d.ratingCount} />
            {d.yearsOfExperience !== null && d.yearsOfExperience !== undefined && (
              <span>
                <Icon name="briefcase-medical" size={13} />
                {t('discovery.card.experience', { n: d.yearsOfExperience })}
              </span>
            )}
          </div>
          <div className={`dc-card__soonest ${d.soonest ? '' : 'is-none'}`}>
            <SoonestLine soonest={d.soonest} />
          </div>
        </>
      )}

      {priceHidden ? (
        <p className="px-5 text-xs text-slate-400">
          <Icon name="info" size={13} className="me-1 inline" />
          {t('discovery.card.priceOnPage')}
        </p>
      ) : remoteMode && d ? (
        <div className="px-5">
          <div className="rounded-xl bg-white/5 p-2.5 text-center">
            <p className="text-[10px] text-slate-400">{t('discovery.card.modePrice', { mode: t(`discovery.mode.${mode}`) })}</p>
            <p className="text-sm font-bold text-white">{formatCurrency(d.price)}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 px-5 text-center">
          <div className="rounded-xl bg-white/5 p-2.5">
            <p className="text-[10px] text-slate-400">{t('discovery.card.exam')}</p>
            <p className="text-sm font-bold text-white">{formatPriceRange(examRange)}</p>
          </div>
          <div className="rounded-xl bg-white/5 p-2.5">
            <p className="text-[10px] text-slate-400">{t('discovery.card.followup')}</p>
            <p className="text-sm font-bold text-white">{formatPriceRange(followupRange)}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 border-t border-white/5 p-4">
        <Link to={`/patient/doctor/${doctor.id}`} className="flex items-center gap-1 text-[11px] font-bold text-slate-300 hover:text-white" data-testid="doctor-profile-link">
          <Icon name="user-round" size={13} />
          {t('discovery.card.profile')}
        </Link>
        <span className="flex items-center gap-1 text-[11px] text-slate-400">
          <Icon name="building-2" size={13} />
          {clinicCount > 0 ? t('discovery.card.clinics', { n: clinicCount }) : t('discovery.card.noClinics')}
        </span>
        <Link
          to={`/patient/book/${doctor.id}${mode ? `?mode=${mode}` : ''}`}
          className={clinicCount > 0 ? 'btn-primary' : 'btn-ghost pointer-events-none opacity-50'}
          data-testid="book-now"
        >
          <Icon name="calendar-plus" size={16} />
          {t('discovery.card.book')}
        </Link>
      </div>
    </Panel>
  );
}

export default PatientDoctors;
